import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import { postWithAuth, getWithAuth } from 'services/http.service';
import { GET_CHILD_STORES, GET_STORE_TOKEN } from 'services/url.service';
import { getBusinessDetails, refreshUserToken } from 'services/user.service';
import { setAuth, setStoreToken, removeAuth } from 'services/identity.service';

const STORAGE_KEY = 'afto_active_store';

export const ActiveStoreContext = createContext(null);

export default function ActiveStoreProvider({ children }) {
    const { data: session, update } = useSession();
    const [activeStore, setActiveStoreState] = useState(null);
    const [activeStoreToken, setActiveStoreToken] = useState(null);
    const [parentStore, setParentStore] = useState(null);
    const [childStores, setChildStores] = useState([]);
    const [isLoadingStore, setIsLoadingStore] = useState(false);
    const [storeActivationError, setStoreActivationError] = useState(null);
    const [needsChildSetup, setNeedsChildSetup] = useState(
        () => typeof window !== 'undefined' && sessionStorage.getItem('afto_needs_child_setup') === 'true'
    );

    const parentStoreRef = useRef(null);
    const restoredRef = useRef(false);
    // Holds the true parent businessAccountId so it survives child-store session updates.
    const parentBusinessAccountIdRef = useRef(null);

    const isSuperadmin = session?.user?.role === 'superadmin' || session?.user?.storeType === 'multi_store_parent';
    const sessionBusinessAccountId = session?.user?.businessAccountId;

    useEffect(() => {
        if (!isSuperadmin || !session) return;

        // Ensure AUTH cookie is fresh before any API call — session may not be
        // written to the cookie yet if the page component's effect hasn't run first.
        setAuth(session);

        // Only run the initial fetch once — parentStoreRef is populated on first run.
        // After a child-store session update the sessionBusinessAccountId changes to the
        // child's ID, so we guard with the ref to avoid re-fetching with wrong data.
        if (parentStoreRef.current) return;

        const resolvedId = sessionBusinessAccountId;
        if (resolvedId) {
            parentBusinessAccountIdRef.current = resolvedId;
            fetchParentStore(resolvedId);
            fetchChildStores();
        } else {
            // businessAccountId is null in session (stale JWT) — resolve via refresh endpoint.
            // setAuth must complete first so the AUTH cookie is present before the child
            // stores request fires (fetchChildStores reads it via getWithAuth).
            refreshUserToken()
                .then((res) => {
                    const freshId = res?.entity?.businessAccountId;
                    if (freshId) {
                        parentBusinessAccountIdRef.current = freshId;
                        // Write refreshed token to cookie before fetching child stores
                        if (res?.entity?.token) {
                            setAuth({ ...session, accessToken: res.entity.token });
                        }
                        fetchParentStore(freshId);
                        fetchChildStores();
                    }
                })
                .catch(() => {});
        }
    }, [isSuperadmin, session]);

    // Restore saved selection once parent is ready; re-check when children load
    useEffect(() => {
        if (!parentStore || restoredRef.current) return;

        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (parsed.isParent) {
                    applyParentStore(parentStore);
                    restoredRef.current = true;
                    return;
                }
                // Wait for childStores before restoring a child selection
                if (childStores.length > 0) {
                    const stillExists = childStores.find((s) => s.id === parsed.id);
                    if (stillExists) {
                        // Do NOT pre-inject the saved token — it may be stale/expired from a
                        // previous session and would cause 401 on all API calls including the
                        // activateStore request itself. Let activateStore fetch a fresh token.
                        activateStore(parsed.id);
                        restoredRef.current = true;
                        return;
                    }
                }
            } catch (_) {
                localStorage.removeItem(STORAGE_KEY);
            }
        }

        // No saved selection or invalid — default to parent
        applyParentStore(parentStore);
        restoredRef.current = true;
    }, [parentStore, childStores]);

    const applyParentStore = (store) => {
        setStoreToken(null);
        setActiveStoreToken(null);
        setActiveStoreState(store);
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ id: store.id, isParent: true }));
    };

    const fetchParentStore = async (id) => {
        try {
            const response = await getBusinessDetails(id);
            const account = response?.entity || response?.data?.business_account;

            // Guard: if we accidentally fetched a child store (happens on page refresh
            // while superadmin is in child context — session.user.businessAccountId
            // holds the child's ID at that point), resolve to the real parent and re-fetch.
            const isChild = account?.storeType === 'multi_store_child';
            const realParentId = isChild
                ? (account?.parentBusinessAccountId || account?.parent_business_account_id)
                : id;

            if (isChild && realParentId && realParentId !== id) {
                parentBusinessAccountIdRef.current = realParentId;
                return fetchParentStore(realParentId);
            }

            const store = {
                id:            realParentId || id,
                name:          account?.name || account?.businessName || 'Main Account',
                websiteDomain: account?.website_domain || account?.websiteDomain || null,
                isParent:      true,
            };
            parentStoreRef.current = store;
            setParentStore(store);
        } catch (_) {
            const store = { id, name: 'Main Account', websiteDomain: null, isParent: true };
            parentStoreRef.current = store;
            setParentStore(store);
        }
    };

    const fetchChildStores = async () => {
        try {
            const response = await getWithAuth(GET_CHILD_STORES);
            if (response?.entity) setChildStores(response.entity);
        } catch (_) {
            setChildStores([]);
        }
    };

    const selectParentStore = useCallback(async () => {
        const store = parentStoreRef.current;
        if (!store) return;
        applyParentStore(store);
        const parentId = parentBusinessAccountIdRef.current || store.id;
        if (session?.user) {
            try {
                await update({
                    ...session,
                    user: {
                        ...session.user,
                        businessAccountId: parentId,
                        storeType: 'multi_store_parent',
                        role: 'superadmin',
                    },
                });
            } catch (_) {}
        }
    }, [session, update]);

    const activateStore = useCallback(async (storeId) => {
        setIsLoadingStore(true);
        setStoreActivationError(null);
        try {
            const response = await postWithAuth(GET_STORE_TOKEN, { childStoreId: storeId });
            if (response?.entity?.token) {
                const store = { ...response.entity.store, isParent: false };
                setStoreToken(response.entity.token);
                setActiveStoreToken(response.entity.token);
                setActiveStoreState(store);
                localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...store, token: response.entity.token }));
                if (session?.user) {
                    try {
                        await update({
                            ...session,
                            user: {
                                ...session.user,
                                businessAccountId: store.id,
                                storeType: store.storeType,
                                role: 'superadmin',
                            },
                        });
                    } catch (_) {}
                }
                if (response.entity.store.isConfigured === false) {
                    setNeedsChildSetup(true);
                    sessionStorage.setItem('afto_needs_child_setup', 'true');
                }
            }
        } catch (err) {
            setActiveStoreToken(null);
            setActiveStoreState(null);
            setStoreActivationError(err?.message || 'Failed to switch store. Please try again.');
        } finally {
            setIsLoadingStore(false);
        }
    }, [session, update]);

    const clearActiveStore = useCallback(() => {
        setActiveStoreState(null);
        setActiveStoreToken(null);
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    const clearNeedsChildSetup = useCallback(() => {
        setNeedsChildSetup(false);
        sessionStorage.removeItem('afto_needs_child_setup');
    }, []);

    const logout = useCallback(() => {
        setActiveStoreState(null);
        setActiveStoreToken(null);
        setNeedsChildSetup(false);
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem('afto_needs_child_setup');
        removeAuth();
    }, []);

    const refreshChildStores = useCallback(() => fetchChildStores(), []);

    const refreshParentStore = useCallback(async () => {
        const id = parentBusinessAccountIdRef.current;
        if (id) await fetchParentStore(id);
    }, []);

    return (
        <ActiveStoreContext.Provider
            value={{
                activeStore,
                activeStoreToken,
                parentStore,
                childStores,
                isLoadingStore,
                storeActivationError,
                needsChildSetup,
                activateStore,
                selectParentStore,
                clearActiveStore,
                clearNeedsChildSetup,
                refreshChildStores,
                refreshParentStore,
                logout,
            }}
        >
            {children}
        </ActiveStoreContext.Provider>
    );
}

export const useActiveStore = () => useContext(ActiveStoreContext);
